import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, LogOut, Paperclip, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, API } from "@/lib/api";
import { CATEGORIES, categoryName } from "@/lib/constants";

const EMPTY_FORM = {
  name: "", category: "gourmet-confectionery", tagline: "", description: "",
  moq: 25, featured: false, customization: "", image_url: "",
};

const STATUS_STYLES = {
  New: "bg-gold/15 text-gold-brass border-gold/40",
  Contacted: "bg-blue-50 text-blue-800 border-blue-200",
  Closed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function ProductModal({ initial, productId, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, moq: Number(form.moq) || 0 }).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("image", file);
      if (productId) await api.put(`/admin/products/${productId}`, fd);
      else await api.post("/admin/products", fd);
      toast.success(productId ? "Product updated." : "Product added.");
      onSaved();
    } catch {
      toast.error("Could not save the product.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-gold/20 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/50 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="admin-product-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-cream border border-gold/20 p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-ink">{productId ? "Edit Product" : "Add Product"}</h2>
          <button data-testid="admin-product-modal-close" onClick={onClose} className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow !text-[10px] block mb-1.5">Name *</label>
              <input data-testid="admin-product-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
            </div>
            <div>
              <label className="eyebrow !text-[10px] block mb-1.5">Category</label>
              <select data-testid="admin-product-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow !text-[10px] block mb-1.5">MOQ</label>
              <input data-testid="admin-product-moq-input" type="number" min="1" value={form.moq} onChange={set("moq")} className={inputCls} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-ink/80 cursor-pointer">
                <input data-testid="admin-product-featured-checkbox" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#B38F4B]" />
                Featured on homepage
              </label>
            </div>
          </div>
          <div>
            <label className="eyebrow !text-[10px] block mb-1.5">Tagline</label>
            <input data-testid="admin-product-tagline-input" value={form.tagline} onChange={set("tagline")} className={inputCls} />
          </div>
          <div>
            <label className="eyebrow !text-[10px] block mb-1.5">Description</label>
            <textarea data-testid="admin-product-description-input" rows={3} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="eyebrow !text-[10px] block mb-1.5">Customization options (comma separated)</label>
            <input data-testid="admin-product-customization-input" value={form.customization} onChange={set("customization")} placeholder="Logo embossing, Custom ribbon, ..." className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow !text-[10px] block mb-1.5">Upload image</label>
              <button type="button" data-testid="admin-product-image-upload" onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-gold/40 px-3.5 py-2.5 text-sm text-ink/60 hover:border-gold transition-colors">
                {file ? file.name : "Choose image..."}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="eyebrow !text-[10px] block mb-1.5">...or image URL</label>
              <input data-testid="admin-product-image-url-input" value={form.image_url} onChange={set("image_url")} placeholder="https://..." className={inputCls} />
            </div>
          </div>
          <button data-testid="admin-product-save-button" type="submit" disabled={saving} className="w-full bg-ink text-cream px-6 py-3.5 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 disabled:opacity-60">
            {saving ? "Saving..." : productId ? "Save Changes" : "Add Product"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProductsTab({ products, reload }) {
  const [modal, setModal] = useState(null);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted.");
      reload();
    } catch {
      toast.error("Could not delete the product.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ink-muted">{products.length} products</p>
        <button
          data-testid="admin-add-product-button"
          onClick={() => setModal({ id: null, form: EMPTY_FORM })}
          className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-3 text-sm hover:bg-gold-brass transition-colors duration-400"
        >
          <Plus size={15} /> Add Product
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-card-${p.id}`} className="bg-white border border-gold/10 overflow-hidden">
            <div className="aspect-[4/3] bg-cream-dark overflow-hidden">
              {p.image && <img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2">
                <p className="eyebrow !text-[9px]">{categoryName(p.category)}</p>
                {p.featured && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-gold-brass border border-gold/40 px-2 py-0.5">Featured</span>}
              </div>
              <h3 className="mt-2 font-serif text-lg text-ink">{p.name}</h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50">MOQ {p.moq}</p>
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`admin-edit-product-${p.id}`}
                  onClick={() => setModal({
                    id: p.id,
                    form: {
                      name: p.name, category: p.category, tagline: p.tagline || "",
                      description: p.description || "", moq: p.moq, featured: !!p.featured,
                      customization: (p.customization || []).join(", "),
                      image_url: p.image?.startsWith("http") ? p.image : "",
                    },
                  })}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-gold/30 px-3 py-2 text-xs text-ink hover:border-gold transition-colors"
                >
                  <Pencil size={13} /> Edit
                </button>
                <button
                  data-testid={`admin-delete-product-${p.id}`}
                  onClick={() => remove(p)}
                  className="inline-flex items-center justify-center gap-1.5 border border-red-200 px-3 py-2 text-xs text-red-700 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <ProductModal
          initial={modal.form}
          productId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload(); }}
        />
      )}
    </div>
  );
}

function EnquiriesTab({ enquiries, reload }) {
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? enquiries : enquiries.filter((e) => e.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      reload();
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["All", "New", "Contacted", "Closed"].map((s) => (
          <button
            key={s}
            data-testid={`admin-enquiry-filter-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] border transition-colors duration-300 ${
              filter === s ? "bg-ink text-cream border-ink" : "border-gold/20 text-ink/70 hover:border-gold/50"
            }`}
          >
            {s} {s !== "All" && `(${enquiries.filter((e) => e.status === s).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <p data-testid="admin-enquiries-empty" className="text-sm text-ink-muted py-16 text-center">
          No enquiries here yet. New submissions from the site will appear here instantly.
        </p>
      )}

      <div className="space-y-4">
        {filtered.map((e) => (
          <div key={e.id} data-testid={`admin-enquiry-card-${e.id}`} className="bg-white border border-gold/10 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl text-ink">{e.name} {e.company && <span className="text-ink-muted text-base">· {e.company}</span>}</h3>
                <p className="mt-1 text-sm text-ink-muted">
                  {e.email} · {e.phone}
                </p>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-[0.1em] text-ink/60">
                  {e.occasion && <span className="border border-gold/20 px-2 py-1">{e.occasion}</span>}
                  {e.quantity && <span className="border border-gold/20 px-2 py-1">Qty: {e.quantity}</span>}
                  {e.product_name && <span className="border border-gold/20 px-2 py-1">Re: {e.product_name}</span>}
                </div>
                {e.message && <p className="mt-3 text-sm text-ink/80 leading-relaxed max-w-2xl">{e.message}</p>}
                {e.file_path && (
                  <a
                    data-testid={`admin-enquiry-file-${e.id}`}
                    href={`${API}/files/${e.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold-brass hover:underline"
                  >
                    <Paperclip size={13} /> {e.original_filename || "View attachment"} <ExternalLink size={11} />
                  </a>
                )}
                <p className="mt-3 text-[11px] text-ink/40">
                  {new Date(e.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <select
                data-testid="admin-enquiry-status-toggle"
                value={e.status}
                onChange={(ev) => setStatus(e.id, ev.target.value)}
                className={`border px-3 py-2 text-xs font-medium cursor-pointer ${STATUS_STYLES[e.status] || ""}`}
              >
                {["New", "Contacted", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [checking, setChecking] = useState(true);

  const loadProducts = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const loadEnquiries = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("gratiora_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then(() => {
        loadProducts();
        loadEnquiries();
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate, loadProducts, loadEnquiries]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gratiora_admin_token");
    navigate("/admin/login");
  };

  if (checking) return <main className="min-h-screen bg-cream" />;

  const newCount = enquiries.filter((e) => e.status === "New").length;

  return (
    <main className="min-h-screen bg-cream">
      <div className="border-b border-gold/15 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-xl tracking-[0.3em] font-medium text-ink">
              GRAT<span className="text-gold-brass">I</span>ORA
            </Link>
            <span className="eyebrow !text-[10px]">Admin Studio</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-ink-muted hover:text-ink transition-colors">View site</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-red-700 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex gap-2 mb-10 border-b border-gold/15">
          <button
            data-testid="admin-tab-products"
            onClick={() => setTab("products")}
            className={`px-6 py-3 text-sm tracking-wide transition-colors duration-300 border-b-2 -mb-px ${
              tab === "products" ? "border-gold-brass text-ink font-medium" : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            Products ({products.length})
          </button>
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`px-6 py-3 text-sm tracking-wide transition-colors duration-300 border-b-2 -mb-px ${
              tab === "enquiries" ? "border-gold-brass text-ink font-medium" : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            Enquiries {newCount > 0 && <span className="ml-1 bg-gold/20 text-gold-brass text-[10px] font-mono px-2 py-0.5">{newCount} new</span>}
          </button>
        </div>

        {tab === "products"
          ? <ProductsTab products={products} reload={loadProducts} />
          : <EnquiriesTab enquiries={enquiries} reload={loadEnquiries} />}
      </div>
    </main>
  );
}
